import {
    LuFolder,
    LuFile,
    LuTrash2,
    LuVideo,
    LuImage,
    LuFileText,
    LuChevronUp,
    LuChevronDown
} from "react-icons/lu";
import { useNavigate } from "react-router-dom";
import TableInnerRowsLoading from "../Loaders/TableInnerRowsLoading";
import { BiDownload } from "react-icons/bi";
import { TbFileSad, TbLock } from "react-icons/tb";
import { useLayoutEffect, useRef, useState } from "react";
import FormatDateTime from "../Functions/FormatDate";
import { useLanguage } from "../Translations/LenguageContext";

const StorageTable = ({
    loading,
    data,
    setSelectedItem,
    setShowDeleteItem,
    downloadItem,
    orgData
}) => {
    const {t} = useLanguage()
    const navigate = useNavigate();

    const headers = [t.name, t.created_at, t.sidebar.organizations, "Status", t.sidebar.actions];

    const OrgListCell = ({ organizations }) => {
        const [isExpanded, setIsExpanded] = useState(false);
        const [hasMore, setHasMore] = useState(false);
        const containerRef = useRef(null);

        const checkOverflow = () => {
            const el = containerRef.current;
            if (el) {
                const overflow = el.scrollHeight > 38;
                setHasMore(overflow);
            }
        };

        useLayoutEffect(() => {
            checkOverflow();

            const resizeObserver = new ResizeObserver(() => checkOverflow());
            if (containerRef.current) resizeObserver.observe(containerRef.current);

            return () => resizeObserver.disconnect();
        }, [organizations]);

        if (!organizations || organizations.length === 0) return null;

        if (organizations.includes("personal")) {
            return (
                <div className="custom_cell_org_wrapper">
                    <div className="custom_cell_org_list">
                        <div className="custom_cell_org_pill_no_icon private" title="Personal">
                            <TbLock />
                        </div>
                    </div>
                </div>
            );
        }

        const resolvedOrgs = organizations
            .map(id => orgData?.find(o => o.id === id))
            .filter(Boolean);

        return (
            <div className="custom_cell_org_wrapper">
                <div
                    ref={containerRef}
                    className={`custom_cell_org_list ${isExpanded ? 'custom_cell_org_list_expanded' : ''}`}
                >
                    {hasMore && (
                        <button
                            onClick={() => setIsExpanded(!isExpanded)}
                            className="org_list_toggle"
                            type="button"
                        >
                            {isExpanded ? <LuChevronUp /> : <LuChevronDown />}
                        </button>
                    )}

                    {resolvedOrgs.map((org) => (
                        <div key={org.id} className="custom_cell_org_pill_no_icon">
                            <span className="org_pill_name" title={org.name}>
                                {org.name}
                            </span>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    const getFileIcon = (name) => {
        if (!name) return <LuFile />;

        const ext = name.split(".").pop().toLowerCase();

        if (["mp4", "mov", "avi", "mkv", "webm"].includes(ext)) {
            return <LuVideo />;
        }

        if (["png", "jpg", "jpeg", "gif", "svg", "webp", "bmp"].includes(ext)) {
            return <LuImage />;
        }

        if (ext === "pdf") {
            return <LuFileText />;
        }

        return <LuFile />;
    };

    return (
        <div className="custom_table_container fade-in">
            <table className="custom_table">
                <thead className="custom_table_head">
                    <tr className="custom_table_row">
                        {headers.map((header, index) => (
                            <th key={index} className="custom_table_th">
                                {header}
                            </th>
                        ))}
                    </tr>
                </thead>

                <tbody className="custom_table_body">
                    {loading ? (
                        <TableInnerRowsLoading />
                    ) : (
                        data.length > 0 ? (
                            data.map((item, rowIndex) => (
                                <tr key={item.id || rowIndex} className="custom_table_row">

                                    <td className="custom_table_td td_name" title={item.name}>
                                        <div
                                            className={`storage_name_cell ${item.type === "folder" ? "clickable_folder" : ""}`}
                                            onClick={() => {
                                                if (item.type === "folder") {
                                                    navigate(`/e8console/storage${item.path}/${item.id}`);
                                                }
                                            }}
                                            style={{ cursor: item.type === "folder" ? "pointer" : "default" }}
                                        >
                                            <i className="storage_icon">
                                                {item.type === "folder"
                                                    ? <LuFolder />
                                                    : getFileIcon(item.name)
                                                }
                                            </i>
                                            {" "}
                                            <span className="bold_text">{item.name}</span>
                                        </div>
                                    </td>

                                    <td className="custom_table_td">
                                        {FormatDateTime(item.created_at)}
                                    </td>

                                    <td className="custom_table_td td_orgs">
                                        <OrgListCell organizations={item.organizations} />
                                    </td>

                                    <td className="custom_table_td td_status">
                                        <div className="custom_cell_status">
                                            {item.status === "UPLOADED" ? (
                                                <span className="status_dot active" title="Uploaded correctly"></span>
                                            ) : item.status === "ERROR" ? (
                                                <span className="status_dot error " title="Not uploaded - error"></span>
                                            ) : item.status === "UPLOADING" ? (
                                                <span className="status_dot uploading " title="Uploading..."></span>
                                            ) : (
                                                <span>-</span>
                                            )}
                                        </div>
                                    </td>

                                    <td className="custom_table_td td_actions">
                                        <button
                                            className="remove_file_btn"
                                            onClick={() => { setShowDeleteItem(true); setSelectedItem(item) }}
                                            title="Delete"
                                        >
                                            <LuTrash2 />
                                        </button>
                                        {item.type === "file" && (
                                            <button
                                                className="download_file_btn"
                                                onClick={() => downloadItem(item)}
                                                title="Download file"
                                            >
                                                <BiDownload />
                                            </button>
                                        )}
                                    </td>

                                </tr>
                            ))
                        ) : (
                            <tr className="custom_table_row_empty">
                                <td colSpan={headers.length} className="custom_table_td_empty ">
                                    <div className="custom_table_td_empty_inner fade-in">
                                        <i> <TbFileSad /></i>
                                        {t.no_data}
                                    </div>
                                </td>
                            </tr>
                        )
                    )}
                </tbody>
            </table>
        </div>
    );
};

export default StorageTable;